/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Text, Stack, Skeleton, useMultiStyleConfig} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import Breadcrumb from '../../../components/breadcrumb'
import {remCalc} from '../../../theme/_utils'
import EditorialComponentsFactory from '../../../components/editorial-components/editorial-components-factory'
import LoadingSpinner from '../../../components/loading-spinner'
//import {isServer} from '@salesforce/retail-react-app/app/utils/utils'

const PageHeader = ({category, isLoading, searchQuery, editorialComponents, ...otherProps}) => {
    const styles = useMultiStyleConfig('PageHeader')
    const normalizedSearchQuery = `${searchQuery || ''}`.trim()

    const entries = Array.isArray(editorialComponents) ? editorialComponents : []
    const heroBanner =
        entries?.find(
            (entry) =>
                entry?._content_type_uid === 'plpwhitebanner' ||
                entry?._content_type_uid === 'herobanner',
        ) ?? null
    const filteredEntries = entries?.filter((entry) => {
        return (
            entry?._content_type_uid !== 'herobanner' &&
            entry?._content_type_uid !== 'plpwhitebanner'
        )
    })
    const hasPageHeaderContent = Boolean(category?.name || heroBanner?.body_copy)
    const bannnerTitle = heroBanner?.heading || heroBanner?.textblock?.title
    const hasBannerTitle = bannnerTitle && bannnerTitle !== ''
    return (
        <Box {...otherProps} data-testid="sf-product-list-breadcrumb">
            {category && !normalizedSearchQuery ? (
                // Breadcrumb manages its own horizontal scroll + fade overlays, so it must not be wrapped in another scroll container
                <Breadcrumb categories={category.parentCategoryTree} />
            ) : (
                !category &&
                !normalizedSearchQuery && (
                    <Skeleton my="small.base" h="small.lg" w={{base: '100%', md: remCalc(350)}} />
                )
            )}

            {hasPageHeaderContent && (
                <Flex id="plp-page-header" {...styles.pageHeaderContainer}>
                    <Flex {...styles.pageHeaderColumn}>
                        <Flex>
                            <Box
                                tabIndex={0}
                                aria-label={category?.name}
                                {...styles.categoryTitleBox}
                            >
                                <Text as="h1" {...styles.categoryTitleText}>
                                    {hasBannerTitle ? bannnerTitle : category?.name}
                                </Text>
                            </Box>
                        </Flex>
                        <Flex>
                            {heroBanner && (
                                <Box tabIndex={0} {...styles.heroBannerBox}>
                                    <Box
                                        {...styles.heroBannerText}
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                heroBanner?.body_copy ||
                                                heroBanner?.textblock?.bodycopy,
                                        }}
                                    />
                                </Box>
                            )}
                        </Flex>
                    </Flex>
                </Flex>
            )}
            {filteredEntries &&
                filteredEntries.map((component, key) => (
                    <Box key={component.uid} {...styles.editorialComponentsContainer}>
                        <EditorialComponentsFactory
                            componentType={component?._content_type_uid}
                            {...component}
                            titleAs={
                                component?._content_type_uid === 'plpwhitebanner' ||
                                component?._content_type_uid === 'herobanner'
                                    ? 'h1'
                                    : undefined
                            }
                            wrapper="pageheader"
                            isFirstElement={key === 0}
                            plpCategory={category}
                        />
                    </Box>
                ))}

            {/* Search results title */}
            {normalizedSearchQuery && (
                <>
                    <Stack id="plp-search-results-header" {...styles.searchContainer}>
                        <Stack {...styles.searchContainerTitles}>
                            <Text id="plp-search-results-label" {...styles.searchResultsLabel}>
                                <FormattedMessage
                                    id="product_list.search.results_label"
                                    defaultMessage="Search results"
                                />
                            </Text>
                            {isLoading ? (
                                <Flex {...styles.searchTitle} position="relative" h={0}>
                                    <LoadingSpinner
                                        wrapperStyles={styles.loadingSpinnerWrapper}
                                        spinnerStyles={styles.loadingSpinnerStyles}
                                    />
                                    <Box
                                        id="plp-search-results-spinner"
                                        data-testid="plp-search-results-spinner"
                                    />
                                </Flex>
                            ) : (
                                <Flex {...styles.searchTitle}>
                                    <Text id="plp-search-results-query">
                                        {`“${normalizedSearchQuery}”`}
                                    </Text>
                                </Flex>
                            )}
                        </Stack>
                    </Stack>
                </>
            )}
        </Box>
    )
}

PageHeader.getTemplateName = () => 'page-header'

PageHeader.propTypes = {
    category: PropTypes.object,
    editorialComponents: PropTypes.array,
    isLoading: PropTypes.bool,
    searchQuery: PropTypes.string,
}

export default PageHeader
